import {
    CheckIcon as CheckmarkIcon,
    PinIcon as MapPin,
} from "@wags/ui/product-icons.tsx";

import { cn } from "@wags/ui/ui-utils";
import { ClickableStaticMap } from "@/components/shared/ClickableStaticMap";
import { sentenceCaseFactLabel } from "../../profile/fact-label";
import { factState } from "../../profile/fact-state";
import {
    hasScreeningColumnContent,
    hasWhatToExpectContent,
} from "../../profile/section-content";
import type { Fact } from "../../profile/types";
import type { ProviderLocationInput } from "../provider-locations";
import { OtherLocations } from "../provider-locations-column";

const UNKNOWN_LIST = new Intl.ListFormat("en", { type: "disjunction" });

export function WhatToExpectSection({
    breederName,
    meetupLocationLabel,
    distanceLabel,
    staticMapSrc,
    coordinates,
    screeningFacts,
    locations,
}: {
    breederName: string;
    meetupLocationLabel: string;
    distanceLabel: string;
    staticMapSrc: string | null;
    coordinates?: { lat: number; lng: number } | null;

    screeningFacts?: Fact[];
    locations: readonly ProviderLocationInput[];
}) {
    const statedScreening =
        screeningFacts?.filter((fact) => factState(fact) === "yes") ?? [];

    const unknownScreening =
        screeningFacts?.filter((fact) => factState(fact) === "unknown") ?? [];
    const unknownList =
        unknownScreening.length > 0
            ? UNKNOWN_LIST.format(
                  unknownScreening.map((fact) =>
                      sentenceCaseFactLabel(fact.label),
                  ),
              )
            : null;

    const hasLocationColumn = coordinates != null;
    const hasScreeningColumn = hasScreeningColumnContent(screeningFacts);
    if (!hasWhatToExpectContent({ coordinates, screeningFacts })) {
        return null;
    }
    return (
        <section className="lg:grid lg:grid-cols-[280px_minmax(0,1fr)] lg:items-start lg:gap-x-16">
            <h2 className="text-section-title text-doberman-black text-balance">
                What to expect
            </h2>
            <div
                className={cn(
                    "mt-5 grid gap-8 md:items-start lg:mt-0 lg:max-w-190",
                    hasLocationColumn && hasScreeningColumn && "md:grid-cols-2",
                )}
            >
                {hasLocationColumn && (
                    <div>
                        <h3 className="text-body-small font-medium text-doberman-black/55">
                            Location &amp; pickup
                        </h3>
                        {staticMapSrc && coordinates ? (
                            <ClickableStaticMap
                                staticMapSrc={staticMapSrc}
                                alt={`Map showing ${breederName} location`}
                                coordinates={coordinates}
                                className="mt-3 aspect-2/1 w-full rounded-card"
                            />
                        ) : (
                            <div className="mt-3 flex aspect-2/1 w-full items-center justify-center rounded-card bg-doberman-black/5">
                                <MapPin className="size-6 text-doberman-black/20" />
                            </div>
                        )}
                        <p className="mt-3">
                            <span className="text-label text-doberman-black">
                                Meet in {meetupLocationLabel}
                            </span>
                            <br />
                            <span className="mt-2 text-body text-doberman-black/70">
                                {distanceLabel}
                            </span>
                        </p>
                        <OtherLocations
                            locations={locations}
                            shownLabel={meetupLocationLabel}
                        />
                    </div>
                )}
                {hasScreeningColumn && (
                    <div>
                        <h3 className="text-body-small font-medium text-doberman-black/55">
                            How they screen
                        </h3>
                        {statedScreening.length > 0 && (
                            <ul className="mt-3 flex flex-col gap-2.5">
                                {statedScreening.map((fact) => (
                                    <li
                                        key={fact.key}
                                        className="flex items-center gap-2.5"
                                    >
                                        <CheckmarkIcon
                                            aria-hidden
                                            className="size-4 shrink-0 text-doberman-black/60"
                                        />
                                        <span className="text-body text-doberman-black/80">
                                            {fact.label}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        )}
                        {unknownList && (
                            <p className="mt-3 text-body-small text-doberman-black/55">
                                We couldn&rsquo;t confirm {unknownList}. Not a
                                no, just unconfirmed.
                            </p>
                        )}
                    </div>
                )}
            </div>
        </section>
    );
}
